import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { FaCheck, FaTimes } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { SubscriptionPlan } from '../types/subscription';
import { useLanguage } from '../i18n/LanguageContext';
import { getUserSubscription as getCurrentSubscription } from '../services/subscription';
import { requestCheckoutSession } from '../services/backendApi';
import { trackLandingEvent, withStoreUtm } from '../services/analytics';

const STORE_URL = withStoreUtm('pricing_free_trial');

// 定价页面容器
const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 120px 20px 80px;
`;

// 标题部分
const HeaderSection = styled.div`
  text-align: center;
  margin-bottom: 60px;
`;

const Title = styled.h1`
  font-size: 2.8rem;
  font-weight: 700;
  margin-bottom: 16px;
  color: var(--color-text-primary);
`;

const Subtitle = styled.p`
  font-size: 1.1rem;
  color: var(--color-text-secondary);
  max-width: 700px;
  margin: 0 auto;
`;

// 计划卡片区域
const PlansContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 30px;
`;

const PlanCard = styled.div<{ $popular?: boolean }>`
  flex: 1;
  min-width: 300px;
  max-width: 350px;
  border: ${({ $popular }) => ($popular ? '2px solid var(--color-accent)' : `1px solid var(--color-border)`)};
  border-radius: var(--radius-medium);
  padding: 30px;
  background-color: var(--color-surface);
  position: relative;
  box-shadow: ${({ $popular }) => ($popular ? 'var(--shadow-medium)' : 'var(--shadow-soft)')};
  transition: transform 0.3s ease, box-shadow 0.3s ease;

  &:hover {
    transform: translateY(-5px);
    box-shadow: ${({ $popular }) =>
      $popular ? '0 22px 45px rgba(63, 118, 255, 0.18)' : '0 18px 30px rgba(15, 23, 42, 0.08)'};
  }
`;

const PopularBadge = styled.div`
  position: absolute;
  top: -12px;
  right: 20px;
  background: linear-gradient(135deg, var(--color-accent), var(--color-accent-dark));
  color: #fff;
  font-size: 0.8rem;
  font-weight: 600;
  padding: 4px 12px;
  border-radius: 12px;
`;

const TrialBadge = styled(PopularBadge)`
  background: linear-gradient(135deg, #34d399, #10b981);
`;

const PlanName = styled.h2`
  font-size: 1.5rem;
  font-weight: 700;
  margin-bottom: 10px;
  color: var(--color-text-primary);
`;

const PlanDescription = styled.p`
  font-size: 0.9rem;
  color: var(--color-text-secondary);
  margin-bottom: 20px;
  min-height: 40px;
`;

const PriceContainer = styled.div`
  margin-bottom: 20px;
`;

const Price = styled.span`
  font-size: 2.5rem;
  font-weight: 700;
  color: var(--color-text-primary);
`;

const PriceUnit = styled.span`
  font-size: 1rem;
  color: var(--color-text-muted);
  margin-left: 4px;
`;

const FeaturesList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 30px 0;
`;

const FeatureItem = styled.li`
  display: flex;
  align-items: center;
  margin-bottom: 12px;
  font-size: 0.9rem;
  color: var(--color-text-primary);
`;

const FeatureIcon = styled.span<{ $included: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background-color: ${({ $included }) => ($included ? 'rgba(63, 118, 255, 0.12)' : 'rgba(233, 76, 111, 0.15)')};
  color: ${({ $included }) => ($included ? 'var(--color-accent-dark)' : '#e94c6f')};
  margin-right: 10px;
  font-size: 0.7rem;
`;

const FeatureText = styled.span<{ $included: boolean }>`
  text-decoration: ${({ $included }) => ($included ? 'none' : 'line-through')};
  color: ${({ $included }) => ($included ? 'var(--color-text-secondary)' : 'var(--color-text-muted)')};
`;

const SubscribeButton = styled.button<{ $primary?: boolean }>`
  width: 100%;
  padding: 12px;
  border-radius: var(--radius-small);
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.3s;
  border: ${({ $primary }) => ($primary ? 'none' : `1px solid var(--color-accent)`)};
  background: ${({ $primary }) => ($primary ? 'linear-gradient(135deg, var(--color-accent), var(--color-accent-dark))' : 'transparent')};
  color: ${({ $primary }) => ($primary ? '#fff' : 'var(--color-accent)')};

  &:hover {
    background: ${({ $primary }) => ($primary ? 'var(--color-accent-dark)' : 'rgba(63, 118, 255, 0.1)')};
    box-shadow: ${({ $primary }) => ($primary ? 'var(--shadow-medium)' : 'none')};
  }

  &:disabled {
    background-color: var(--color-border);
    cursor: not-allowed;
    color: var(--color-text-muted);
    border: 1px solid var(--color-border);
  }
`;

const MessageContainer = styled.div<{ type: 'info' | 'error' | 'success' }>`
  background-color: ${({ type }) =>
    type === 'info'
      ? 'rgba(63, 118, 255, 0.08)'
      : type === 'error'
        ? 'rgba(233, 76, 111, 0.1)'
        : 'rgba(34, 197, 94, 0.08)'};
  border-left: 4px solid ${({ type }) =>
    type === 'info'
      ? 'var(--color-accent)'
      : type === 'error'
        ? '#e94c6f'
        : '#22c55e'};
  padding: 10px;
  margin-bottom: 20px;
`;

const Pricing: React.FC = () => {
  const { t } = useLanguage();
  const [isLoading, setIsLoading] = useState<string | null>(null);
  const { user, subscriptionStatus } = useAuth();
  const navigate = useNavigate();
  const [message, setMessage] = useState<{ text: string; type: 'info' | 'error' | 'success' } | null>(null);
  const [subscriptionData, setSubscriptionData] = useState<any>(null);
  const pricingViewTrackedRef = useRef(false);

  // 输出调试信息
  useEffect(() => {
    if (user && subscriptionStatus) {
      console.log('用户订阅状态:', {
        isActive: subscriptionStatus.isActive,
        status: subscriptionStatus.status,
        planId: subscriptionStatus.planId,
        periodEnd: subscriptionStatus.periodEnd ? new Date(subscriptionStatus.periodEnd).toLocaleString() : null,
        cancelAtPeriodEnd: subscriptionStatus.cancelAtPeriodEnd
      });
    }
  }, [user, subscriptionStatus]);

  // 从后端直接获取订阅状态
  useEffect(() => {
    const fetchSubscriptionData = async () => {
      if (!user) return;

      try {
        const result = await getCurrentSubscription();

        if (result.subscription) {
          setSubscriptionData(result.subscription);
        } else {
          setSubscriptionData(null);
        }
      } catch (error) {
        console.error('Failed to fetch subscription data:', error);
      }
    };

    if (user) {
      fetchSubscriptionData();
    }
  }, [user]);

  // 全面检查用户是否有有效订阅
  const hasValidSubscription = () => {
    // 方法1: 通过 AuthContext 中的 subscriptionStatus 检查
    if (subscriptionStatus && (
      subscriptionStatus.isActive ||
      subscriptionStatus.status === 'active' ||
      subscriptionStatus.status === 'active_canceling'
    )) {
      return true;
    }

    // 方法2: 通过直接从后端获取的订阅数据检查
    if (subscriptionData && (
      subscriptionData.status === 'active' ||
      subscriptionData.status === 'active_canceling' ||
      subscriptionData.subscription_status === 'active' ||
      subscriptionData.subscription_status === 'active_canceling'
    )) {
      return true;
    }

    return false;
  };

  useEffect(() => {
    if (pricingViewTrackedRef.current) {
      return;
    }

    pricingViewTrackedRef.current = true;
    trackLandingEvent('pricing_view', {
      logged_in: Boolean(user),
      has_active_subscription: hasValidSubscription(),
      trial_status: subscriptionData?.subscription_status === 'trialing' || subscriptionData?.status === 'trialing'
        ? 'trialing'
        : subscriptionData?.subscription_status || subscriptionData?.status || 'unknown'
    });
  }, [user, subscriptionData]);

  // 使用translations中的plans数据
  const pricingPlans = t('pricing.plans', { returnObjects: true }) as Array<{
    name: string;
    price: string;
    features: string[];
    buttonText: string;
  }>;

  // 构建订阅计划
  const plans: SubscriptionPlan[] = [
    {
      id: 'free-trial',
      name: pricingPlans[0].name,
      description: t('pricing.trialDescription'),
      price: 0,
      currency: 'USD',
      interval: 'month',
      features: pricingPlans[0].features.map(feature => ({
        name: feature,
        included: true
      })),
      priceId: '',
      trial: true,
    },
    {
      id: 'pro-yearly',
      name: pricingPlans[1].name,
      description: t('plans.pro-yearly.description'),
      price: 9.9,
      currency: 'USD',
      interval: 'year',
      features: pricingPlans[1].features.map(feature => ({
        name: feature,
        included: true
      })),
      priceId: 'price_pro_yearly',
    },
    {
      id: 'pro-lifetime',
      name: pricingPlans[2].name,
      description: t('plans.pro-lifetime.description'),
      price: 19.9,
      currency: 'USD',
      interval: 'year',
      features: pricingPlans[2].features.map(feature => ({
        name: feature,
        included: true
      })),
      priceId: 'price_pro_lifetime',
      lifetime: true,
    },
  ];

  // 处理计划选择
  const handlePlanSelect = async (plan: SubscriptionPlan) => {
    trackLandingEvent('plan_select', {
      plan_id: plan.id,
      logged_in: Boolean(user),
      has_active_subscription: hasValidSubscription()
    });

    if (plan.id === 'free-trial') {
      if (!user) {
        navigate('/login?from=pricing&intent=trial');
        return;
      }

      setMessage({
        text: t('pricing.trialReady'),
        type: 'success'
      });
      trackLandingEvent('store_cta_click', { placement: 'pricing_free_trial', plan_id: plan.id });
      window.open(STORE_URL, '_blank', 'noopener,noreferrer');
      return;
    }

    if (!user) {
      // 用户未登录，引导至登录页面
      navigate('/login?from=pricing');
      return;
    }

    // 使用全面的订阅状态检查函数
    if (hasValidSubscription()) {
      // 显示提示信息
      setMessage({
        text: t('pricing.alreadySubscribed'),
        type: 'info'
      });

      // 延迟一秒后跳转，让用户有时间看到提示信息
      setTimeout(() => {
        navigate('/mySubscription');
      }, 1000);
      return;
    }

    // 设置加载状态
    setIsLoading(plan.id);

    try {
      // 确定lookup_key
      let lookupKey;
      if (plan.id === 'pro-monthly') {
        lookupKey = 'pro_monthly';
      } else if (plan.id === 'pro-yearly') {
        lookupKey = 'pro_yearly';
      } else if (plan.id === 'pro-lifetime') {
        lookupKey = 'pro_lifetime';
      }

      // 构建成功和取消的重定向URL
      const successUrl = `${window.location.origin}/subscription-success`;
      const cancelUrl = `${window.location.origin}/subscription-canceled`;

      if (!lookupKey) {
        throw new Error(t('pricing.missingPlan'));
      }

      trackLandingEvent('checkout_start', { plan_id: plan.id, lookup_key: lookupKey });
      const data = await requestCheckoutSession({
        lookup_key: lookupKey,
        success_url: successUrl,
        cancel_url: cancelUrl,
        user_id: user.uid
      });

      // 检查响应中是否包含URL
      if (data && data.url) {
        trackLandingEvent('checkout_redirect', { plan_id: plan.id, lookup_key: lookupKey });
        // 重定向到Stripe结账页面
        window.location.href = data.url;
      } else {
        throw new Error(t('pricing.invalidCheckoutUrl'));
      }
    } catch (error) {
      console.error('Checkout error:', error);
      trackLandingEvent('checkout_error', { plan_id: plan.id });
      setMessage({
        text: t('pricing.checkoutFailed'),
        type: 'error'
      });
      setIsLoading(null);
    }
  };

  // 获取特定计划的价格显示
  const getPriceDisplay = (plan: SubscriptionPlan) => {
    if (plan.price === 0) {
      if (plan.trial) {
        return t('pricing.free');
      }
      return t('pricing.free');
    }

    const currency = plan.currency === 'CNY' ? '¥' : '$';
    return `${currency}${plan.price}`;
  };

  // 获取特定计划的按钮文本
  const getButtonText = (plan: SubscriptionPlan) => {
    if (isLoading === plan.id) {
      return t('pricing.processing');
    }

    // 使用全面的订阅状态检查函数
    if (hasValidSubscription()) {
      return t('pricing.manageSubscription');
    }

    // 根据plan.id获取对应的索引
    let buttonIndex = 0;
    if (plan.id === 'free-trial') {
      buttonIndex = 0;
    } else if (plan.id === 'pro-yearly') {
      buttonIndex = 1;
    } else if (plan.id === 'pro-lifetime') {
      buttonIndex = 2;
    }

    return pricingPlans[buttonIndex].buttonText;
  };

  return (
    <Container>
      <HeaderSection>
        <Title>{t('pricing.title')}</Title>
        <Subtitle>
          {t('pricing.subtitle')}
        </Subtitle>

        {message && (
          <MessageContainer type={message.type}>
            {message.text}
          </MessageContainer>
        )}
      </HeaderSection>

      <PlansContainer>
        {plans.map((plan) => (
          <PlanCard key={plan.id} $popular={plan.id === 'pro-yearly'}>
            {plan.id === 'pro-yearly' && <PopularBadge>{t('pricing.mostPopular')}</PopularBadge>}
            {plan.trial && <TrialBadge>{t('pricing.limitedTime')}</TrialBadge>}

            <PlanName>{plan.name}</PlanName>
            <PlanDescription>{plan.description}</PlanDescription>

            <PriceContainer>
              <Price>
                {getPriceDisplay(plan)}
              </Price>
              {plan.price > 0 && (
                <PriceUnit>
                  {plan.lifetime ?
                    `/${t('pricing.lifetime')}` :
                    `/${plan.interval === 'month' ? t('pricing.mo') : t('pricing.yr')}`
                  }
                </PriceUnit>
              )}
            </PriceContainer>

            <FeaturesList>
              {plan.features.map((feature, index) => (
                <FeatureItem key={index}>
                  <FeatureIcon $included={feature.included}>
                    {feature.included ? <FaCheck size={10} /> : <FaTimes size={10} />}
                  </FeatureIcon>
                  <FeatureText $included={feature.included}>
                    {feature.name}
                  </FeatureText>
                </FeatureItem>
              ))}
            </FeaturesList>

            {plan.id === 'free-trial' ? (
              // 免费试用按钮
              <SubscribeButton
                $primary={false}
                onClick={() => handlePlanSelect(plan)}
                disabled={isLoading === plan.id}
              >
                {getButtonText(plan)}
              </SubscribeButton>
            ) : (
              // 付费计划使用API调用代替表单提交
              <SubscribeButton
                $primary={true}
                onClick={() => handlePlanSelect(plan)}
                disabled={isLoading === plan.id || !user}
              >
                {getButtonText(plan)}
              </SubscribeButton>
            )}
          </PlanCard>
        ))}
      </PlansContainer>
    </Container>
  );
};

export default Pricing;
